/* eslint-disable no-underscore-dangle */
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { WRAPPER_TO_PROVIDER_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import { Provider } from '@/features/game-context/types';
import { handleUnknownEvent } from '@/features/game-window-integration/helpers/generic-helpers';
import {
  isPlaytechCloseFrameMessage,
  parsePlaytechMessageData,
} from '@/features/game-window-integration/providers/playtech/helpers';
import {
  PlaytechMessageUcip,
  PlaytechRecevingEvents,
  PlaytechSendingEvents,
  UcipMessageEvent,
} from '@/features/game-window-integration/providers/playtech/types';
import usePlaytechHandlers from '@/features/game-window-integration/providers/playtech/use-playtech-handlers';
import {
  gameWindowHelperDataSelector,
  setErrorCorrelationId,
} from '@/features/game-window-integration/store/game-window-helper-data';
import { ProviderLoggerMessages } from '@/features/game-window-integration/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';

export const usePlaytechEventHandlers = (provider: Provider) => {
  const dispatch = useDispatch();
  const correlationId = useSelector(gameWindowHelperDataSelector);

  const {
    handleIntialization,
    handleGameReady,
    handleGameLoadStarted,
    handleGameLoadProgress,
    handleSound,
    handleSpinInProgress,
    handleSwitchGame,
    handleError,
    handleNotImplemented,
    getGameProviderFrame,
    volumeLevel,
    handleGameChange,
    handleCloseGame,
    handleSendCloseGameRequest,
  } = usePlaytechHandlers(provider);

  const providerEventHandlerMapper = useMemo(
    () => ({
      [PlaytechRecevingEvents.INITIALIZATION_REQUEST]: handleIntialization,
      [PlaytechRecevingEvents.SOUND_UPDATE]: handleSound,
      [PlaytechRecevingEvents.GAME_LOAD_STARTED]: handleGameLoadStarted,
      [PlaytechRecevingEvents.GAME_LOAD_PROGRESS]: handleGameLoadProgress,
      [PlaytechRecevingEvents.GAME_LOAD_COMPLETED]: handleNotImplemented,
      [PlaytechRecevingEvents.GAME_READY]: handleGameReady,
      [PlaytechRecevingEvents.AUTOPLAY_START]: handleNotImplemented,
      [PlaytechRecevingEvents.AUTOPLAY_END]: handleNotImplemented,
      [PlaytechRecevingEvents.ROUND_START]: handleSpinInProgress,
      [PlaytechRecevingEvents.ROUND_END]: handleSpinInProgress,
      [PlaytechRecevingEvents.GAME_STATE]: handleNotImplemented,
      [PlaytechRecevingEvents.SWITCH_GAME]: handleSwitchGame,
      [PlaytechRecevingEvents.ERROR_DELEGATION]: handleError,
      [PlaytechRecevingEvents.CLOSE_GAME_RESPONSE]: handleCloseGame,
      [PlaytechRecevingEvents.CLOSE_GAME_FRAME_COMMAND]: handleCloseGame,
      [PlaytechRecevingEvents.OPEN_NOTIFICATION]: handleGameChange,
      [PlaytechRecevingEvents.GAME_BALANCE_UPDATE_NOTIFICATION]:
        handleNotImplemented,
      [PlaytechRecevingEvents.GAME_CLOSE_NOTIFICATION]: handleNotImplemented,
    }),
    [
      handleIntialization,
      handleSound,
      handleGameLoadStarted,
      handleGameLoadProgress,
      handleNotImplemented,
      handleGameReady,
      handleSpinInProgress,
      handleSwitchGame,
      handleError,
      handleGameChange,
      handleCloseGame,
    ],
  );

  const providerEventHandler = useCallback(
    (event: UcipMessageEvent) => {
      const { source } = event ?? {};

      if (!source) return;

      const parsedEventData = parsePlaytechMessageData(event?.data);
      if (!parsedEventData) return;

      const normalized = isPlaytechCloseFrameMessage(parsedEventData)
        ? { _type: PlaytechRecevingEvents.CLOSE_GAME_FRAME_COMMAND }
        : parsedEventData;

      const newEventObject = {
        rawEvent: event,
        data: normalized as PlaytechMessageUcip,
      };

      const messageType = newEventObject.data?._type;
      if (messageType && providerEventHandlerMapper[messageType]) {
        providerEventHandlerMapper[messageType]?.(newEventObject);
      } else {
        handleUnknownEvent(newEventObject);
      }
    },
    [providerEventHandlerMapper],
  );

  const wrapperEventHandler = useCallback(
    (e: MessageEvent) => {
      const frame = getGameProviderFrame();

      const executeEvent = (event: PlaytechMessageUcip) => {
        if (frame) {
          const eventJSON = JSON.stringify(event);
          const options = { targetOrigin: '*' };
          frame?.postMessage(eventJSON, options);
        } else {
          Logger.rumError({
            name: ProviderLoggerMessages.FAILED_HANDLER_FETCH.replace(
              '%{provider}',
              Provider.PLAYTECH,
            ),
            useCase: DdEventCategory.ProviderIntegration,
            eventType: DdEventType.Error,
            context: {
              provider: Provider.PLAYTECH,
            },
          });
        }
      };

      const eventResponseMapper = {
        [WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_AUDIO]: () => {
          executeEvent({
            _type: PlaytechSendingEvents.SOUND_UPDATE,
            mute: !e?.data?.payload,
            ...(volumeLevel === 0 && e?.data?.payload && { level: 100 }),
          });
          WrapperProxy.setAudio(e?.data?.payload);
        },
        [WRAPPER_TO_PROVIDER_EVENT_TYPES.RELOAD_BALANCE]: () => {
          executeEvent({
            _type: PlaytechSendingEvents.REFRESH_BALANCE,
          });
        },
        [WRAPPER_TO_PROVIDER_EVENT_TYPES.DISMISS_ERROR]: () => {
          executeEvent({
            _type: PlaytechSendingEvents.ERROR_DELEGATION,
            correlationId,
          });
          dispatch(setErrorCorrelationId(null));
        },
        [WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_STOP]: () => {
          executeEvent({
            _type: PlaytechSendingEvents.AUTO_SPIN_STOP,
          });
        },
        [WRAPPER_TO_PROVIDER_EVENT_TYPES.FORCE_END_SESSION]: () => {
          handleSendCloseGameRequest();
        },
      };

      eventResponseMapper[e.data.type]?.();
    },
    [
      getGameProviderFrame,
      volumeLevel,
      correlationId,
      dispatch,
      handleSendCloseGameRequest,
    ],
  );

  return { wrapperEventHandler, providerEventHandler };
};
